// InstantMessageList - MODULE
// ***********************************************************************************************************************
import downloadjs from "downloadjs";
import { Translations } from "ui/Scripts/source/models/translations";
import * as attachmentService from "../../models/services/attachmentService";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { FileType, UserType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "../../framework/appUtils";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";

// InstantMessageAttachmentImage
export interface IInstantMessageAttachmentImageProp {
    attachment: IFileAttachment;
    selectedId: string;
    onClick: (id: string) => void;
}

export class InstantMessageAttachmentImage extends React.Component<IInstantMessageAttachmentImageProp, {}> {
    render() {
        const props = this.props;
        const attachment = props.attachment;
        return (
            <div className={"attachmentImage" + (attachment.id === props.selectedId ? " selected" : "")} onClick={() => { props.onClick(attachment.id); }} title={attachment.name + " (" + Base.intToFileSizeStr(attachment.size, false) + ", " + Base.timeToDateStr(attachment.modifyDate) + ")"}>
                <img src={attachment.thumbnail} alt={attachment.name}/>
            </div>
        );
    }
}

// InstantMessageAttachment
export interface IInstantMessageAttachmentProp {
    attachment: IFileAttachment;
    selectedId: string;
    onClick: (id: string) => void;
}

export class InstantMessageAttachment extends React.Component<IInstantMessageAttachmentProp, {}> {
    render() {
        const props = this.props;
        const attachment = props.attachment;
        return (
            <div className={"attachment" + (attachment.id === props.selectedId ? " selected" : "")} onClick={(e) => { e.stopPropagation(); props.onClick(attachment.id); }} title={attachment.name + " (" + Base.intToFileSizeStr(attachment.size, false) + ", " + Base.timeToDateStr(attachment.modifyDate) + ")"}>
                <span className={"icon file file_type_" + attachment.type}></span>
                <span className="name">{attachment.name + " (" + Base.intToFileSizeStr(attachment.size, false) + ")"}</span>
            </div>
        );
    }
}

// InstantMessageListLine
export interface IInstantMessageListLineProp {
    observer: IntersectionObserver;
    channelId: string;
    channelIsReadOnly: boolean;
    userCode: string;
    userIsChannelAdmin: boolean;
    selectedId: string;
    item: IInstantMessageListItem;
    onOpenMenu: () => void;
}

export class InstantMessageListLine extends React.Component<IInstantMessageListLineProp, {}> {
    private lineDiv: HTMLDivElement;

    componentDidMount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv || !props.item || props.item.isTitle || props.item.modifyUser.code === props.userCode || props.item.modifyUser.isRemoved()) return;
        props.observer.observe(this.lineDiv);
    }

    componentWillUnmount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv) return;
        props.observer.unobserve(this.lineDiv);
    }

    handleAttachmentClick = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const attachment = props.item.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };
    
    render() {
        const props = this.props;
        const item = props.item;
        if (item.isTitle) {
            return (
                <div className={"instantMessageLine title"}>
                    <div className="instantMessageLineTitle">{item.content}</div>
                </div>
            );
        }
        const isDeleted = item.isDeleted();
        const modifyUserIsDeleted = item.modifyUser.isRemoved();
        const isMyMessage = props.userCode === item.modifyUser.code;
        const imageAttachments = isDeleted ? [] : item.attachments.filter(i => i.type === FileType.Image);
        const fileAttachments = isDeleted ? [] : item.attachments.filter(i => i.type !== FileType.Image);
        const replyUserIsDeleted = item.replyToInstantMessage ? item.replyToInstantMessage.modifyUser.userType === UserType.Removed : false;
        return (
            <div className={"instantMessageLine data id" + item.id + (isMyMessage ? " myMessage" : "") + (isDeleted ? " removed" : "") + (props.selectedId === item.id ? " selected" : "")}
                ref={(elem) => { this.lineDiv = elem; }} data-id={item.id}>
                {!isMyMessage &&
                    <AvatarImage
                        id={item.modifyUser.code}
                        name={item.modifyUser.name}
                        image={item.modifyUser.picture}
                        tooltip={item.modifyUser.name}
                    />
                }
                <div className="instantMessageLineItem">
                    {!!item.replyToInstantMessage &&
                        <div className="replyTo">
                            <div className="sender">
                                <span className={replyUserIsDeleted ? "disabled" : ""}>{item.replyToInstantMessage.modifyUser.name}&nbsp;</span>{Base.timeToTimeStr(item.replyToInstantMessage.modifyUser.date)}
                            </div>
                            <div className="imTextContainer" dangerouslySetInnerHTML={ { __html: Base.linkifyText(item.replyToInstantMessage.getText(props.userCode)) } }></div>
                        </div>
                    }
                    <div className="content">
                        {imageAttachments.length > 0 &&
                            <div className="imAttachmentImages">
                                {imageAttachments.map((attachment) =>
                                    <InstantMessageAttachmentImage
                                        key={attachment.id}
                                        attachment={attachment}
                                        selectedId={""}
                                        onClick={this.handleAttachmentClick}
                                    />
                                )}
                            </div>
                        }
                        {fileAttachments.length > 0 &&
                            <div className="imAttachments">
                                {fileAttachments.map((attachment) =>
                                    <InstantMessageAttachment
                                        key={attachment.id}
                                        attachment={attachment}
                                        selectedId={""}
                                        onClick={this.handleAttachmentClick}
                                    />
                                )}
                            </div>
                        }
                        {(!!item.content || isDeleted) &&
                            <div className="imTextContainer">
                                <div className="imText" style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={ { __html: Base.linkifyText(item.getText(props.userCode)) } }></div>
                            </div>
                        }
                        <div className="modifyDate">
                            <div className="icon openMenu threeDots gray" onClick={props.onOpenMenu}></div>
                            <div className="nameContainer"><span className={modifyUserIsDeleted ? "disabled" : ""}>{item.modifyUser.name}&nbsp;</span>{Base.timeToTimeStr(item.modifyUser.date)}</div>
                            {!isDeleted && isMyMessage && !item.isNotRead() &&
                                <span className={"icon doubleCheck " + (item.isSomeRead() ? "gray" : "blue")} title={item.isAllRead() ? Translations.AllInstantMessageUsersHaveReadMessage : Translations.SomeInstantMessageUsersHaveReadMessage} ></span>
                            }
                            {!isDeleted && !modifyUserIsDeleted && !isMyMessage && !item.isUserRead() &&
                                <span className="icon newItem" title={Translations.Unread}></span>
                            }
                        </div>
                    </div>
                </div>
                {isMyMessage &&
                    <AvatarImage
                        classes="right noAuto"
                        id={item.modifyUser.code}
                        name={item.modifyUser.name}
                        image={item.modifyUser.picture}
                        tooltip={item.modifyUser.name}
                    />
                }
            </div>
        );
    }
}